import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './ModalWindow.css';

const ModalWindow = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  position = { x: 0, y: 0 },
  isDraggable = true,
  onDragEnd,
  headerContent,
  zIndex = 9999,
  onFocus
}) => {
  const [localPosition, setLocalPosition] = useState(position);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // Initialize position only once when the modal first opens
  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened with position:', position);
      if (position) {
        console.log('Setting modal position to:', position);
        setLocalPosition(position);
      } else {
        console.warn('No position provided for modal');
      }
    }
  }, [isOpen, position]);

  // Keep modal within viewport on window resize
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      setLocalPosition(prev => {
        const modalWidth = 500; // Same as in CSS
        const modalHeight = 600; // Approximate modal height
        const rightGap = 40; // Match the initial gap from ComponentInspector
        
        return {
          x: Math.min(prev.x, window.innerWidth - modalWidth - rightGap),
          y: Math.min(Math.max(0, prev.y), window.innerHeight - modalHeight)
        };
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  const handleMouseDown = (e) => {
    if (!isDraggable) return;
    if (e.target.closest('.modal-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - localPosition.x,
        y: e.clientY - localPosition.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setLocalPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    if (isDragging && onDragEnd) {
      onDragEnd(localPosition);
    }
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStart]);

  console.log('ModalWindow render:', { isOpen, title, position });
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div 
        ref={modalRef}
        className="modal-window"
        style={{
          transform: `translate(${localPosition.x}px, ${localPosition.y}px)`,
          '--modal-z-index': zIndex
        }}
        onMouseDown={(e) => {
          // Bring to front first
          if (onFocus) onFocus();
          // Then handle dragging
          handleMouseDown(e);
        }}
      >
      <div className="modal-header">
        <div className="modal-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="modal-title">{title}</span>
            {title === "Photo Gallery" && (
              <button
                className="modal-title-button"
                onClick={() => document.getElementById('file-upload').click()}
                title="Upload image"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </button>
            )}
          </div>
          {headerContent}
        </div>
        <button 
          className="modal-close-button"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className="modal-content">
        {children}
      </div>
    </div>
    </>,
    document.body
  );
};

export default ModalWindow;